"use client"

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react'

interface BreadcrumbContextType {
  overrides: Map<string, string>
  setOverride: (path: string, label: string) => void
  clearOverride: (path: string) => void
  clearAllOverrides: () => void
}

const BreadcrumbContext = createContext<BreadcrumbContextType | undefined>(undefined)

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [overrides, setOverrides] = useState<Map<string, string>>(new Map())

  const setOverride = useCallback((path: string, label: string) => {
    setOverrides(prev => {
      const next = new Map(prev)
      next.set(path, label)
      return next
    })
  }, [])

  const clearOverride = useCallback((path: string) => {
    setOverrides(prev => {
      const next = new Map(prev)
      next.delete(path)
      return next
    })
  }, [])

  const clearAllOverrides = useCallback(() => {
    setOverrides(new Map())
  }, [])

  return (
    <BreadcrumbContext.Provider value={{ overrides, setOverride, clearOverride, clearAllOverrides }}>
      {children}
    </BreadcrumbContext.Provider>
  )
}

export function useBreadcrumb() {
  const context = useContext(BreadcrumbContext)
  if (context === undefined) {
    throw new Error('useBreadcrumb must be used within a BreadcrumbProvider')
  }
  return context
}
